/**
 * <be-header>: scroll state for the glass bar + accessible mobile menu drawer.
 */
import { scrollTop, onScrollFrame, trapFocus, lockScroll } from '@be/core';

class BeHeader extends HTMLElement {
  /** @type {(() => void) | null} */ #unsubscribe = null;
  /** @type {(() => void) | null} */ #release = null;
  /** @type {AbortController | null} */ #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#unsubscribe = onScrollFrame(() => this.#update());
    this.#update();

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-be-menu-open]')) this.openMenu();
        else if (target.closest('[data-be-menu-close]')) this.closeMenu();
        else if (target.closest('[data-be-menu-link]')) this.closeMenu(false);
      },
      { signal }
    );

    // Close open desktop dropdowns when clicking elsewhere.
    document.addEventListener(
      'click',
      (event) => {
        this.querySelectorAll('details[open]').forEach((details) => {
          if (!(event.target instanceof Node) || !details.contains(event.target)) details.removeAttribute('open');
        });
      },
      { signal }
    );

    window.matchMedia('(min-width: 990px)').addEventListener(
      'change',
      (event) => {
        if (event.matches) this.closeMenu(false);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#controller?.abort();
    this.closeMenu(false);
  }

  #update() {
    this.toggleAttribute('data-scrolled', scrollTop() > 24);
  }

  get menu() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.be-menu'));
  }

  openMenu() {
    const menu = this.menu;
    if (!menu || menu.hasAttribute('data-open')) return;
    menu.removeAttribute('inert');
    menu.setAttribute('data-open', '');
    this.querySelector('[data-be-menu-open]')?.setAttribute('aria-expanded', 'true');
    lockScroll(this, true);
    this.#release = trapFocus(menu, { onEscape: () => this.closeMenu() });
  }

  /** @param {boolean} [restoreFocus] */
  closeMenu(restoreFocus = true) {
    const menu = this.menu;
    if (!menu || !menu.hasAttribute('data-open')) return;
    menu.removeAttribute('data-open');
    menu.setAttribute('inert', '');
    this.querySelector('[data-be-menu-open]')?.setAttribute('aria-expanded', 'false');
    lockScroll(this, false);
    const release = this.#release;
    this.#release = null;
    if (restoreFocus) release?.();
    else if (release) {
      // Navigation link: release the trap without stealing focus back.
      const active = document.activeElement;
      release();
      if (active instanceof HTMLElement && document.contains(active)) active.blur();
    }
  }
}

if (!customElements.get('be-header')) customElements.define('be-header', BeHeader);